import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import {
  getUserSubscriptionStatsByRestaurantId,
  getUserSubscriptionsByPhoneOrEmail,
  getCustomersByRestaurantId,
} from "../service/userSubscription";
import { getPaymentStatusKey } from "../utils/utils";
import { getRestaurantId } from "../utils/JwtUtil";

// Import modular components
import CustomerStatsCards from "../components/customers/CustomerStatsCards";
import CustomerSearchBar from "../components/customers/CustomerSearchBar";
import CustomerFilterTabs from "../components/customers/CustomerFilterTabs";
import CustomerDetailsModal from "../components/customers/CustomerDetailsModal";
import SubscriptionScheduleMap from "../components/customers/SubscriptionScheduleMap";
import { DataTable } from "../components/common/DataTable";
import { formatDate } from "../utils/utils";

/**
 * @typedef {Object} CustomerSummary
 * @property {number} userId
 * @property {string} fullName
 * @property {string} phone
 * @property {string} subscriptionName
 * @property {string} startDate
 * @property {string} endDate
 * @property {number} remainingDaysCount
 * @property {string} paymentStatus
 * @property {boolean} isFrozenByUser
 * @property {boolean} isActive
 * @property {Object} defaultLocation
 * @property {number} userSubscriptionId
 */

const Customers = () => {
  const { t } = useTranslation();

  // State management
  /** @type {[string, Function]} */
  const [filter, setFilter] = useState("all"); // "all", "active", "completed"
  /** @type {[CustomerSummary|null, Function]} */
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  /** @type {[boolean, Function]} */
  const [showModal, setShowModal] = useState(false);
  /** @type {[number, Function]} */
  const [currentPage, setCurrentPage] = useState(0);
  /** @type {[number, Function]} */
  const [pageSize, setPageSize] = useState(10);
  /** @type {[string, Function]} */
  const [searchQuery, setSearchQuery] = useState("");
  /** @type {[boolean, Function]} */
  const [isSearchMode, setIsSearchMode] = useState(false);
  /** @type {[string, Function]} */
  const [activeSearchQuery, setActiveSearchQuery] = useState("");
  /** @type {[boolean, Function]} */
  const [showMap, setShowMap] = useState(true);
  /** @type {[Date|null, Function]} */
  const [selectedMapDate, setSelectedMapDate] = useState(new Date());

  // Get restaurant ID from JWT token
  const restaurantId = getRestaurantId();

  // React Query for subscription statistics
  const { data: stats } = useQuery({
    queryKey: ["userSubscriptionStats", restaurantId],
    queryFn: () => getUserSubscriptionStatsByRestaurantId(restaurantId),
    staleTime: 5 * 60 * 1000, // 5 minutes
    cacheTime: 10 * 60 * 1000, // 10 minutes
  });

  // React Query for customers data
  const {
    data: customersData,
    isLoading: customersLoading,
    error: customersError,
  } = useQuery({
    queryKey: ["customers", restaurantId, filter, currentPage, pageSize],
    queryFn: async () => {
      const response = await getCustomersByRestaurantId(
        restaurantId,
        currentPage,
        pageSize
      );
      return response;
    },
    staleTime: 2 * 60 * 1000, // 2 minutes
    cacheTime: 5 * 60 * 1000, // 5 minutes
    keepPreviousData: true,
    enabled: !isSearchMode,
  });

  // React Query for search functionality
  const {
    data: searchData,
    isLoading: searchLoading,
    error: searchError,
  } = useQuery({
    queryKey: ["searchCustomers", activeSearchQuery],
    queryFn: async () => {
      const response = await getUserSubscriptionsByPhoneOrEmail(
        activeSearchQuery
      );
      return {
        content: response,
        totalElements: response.length,
        totalPages: 1,
        number: 0,
        size: response.length,
      };
    },
    enabled: isSearchMode && activeSearchQuery.trim().length > 0,
    staleTime: 1 * 60 * 1000,
    cacheTime: 3 * 60 * 1000,
  });

  // Extract data from React Query response with useMemo to avoid dependency issues
  const customers = React.useMemo(() => {
    return isSearchMode
      ? searchData?.content || []
      : customersData?.content || [];
  }, [isSearchMode, searchData?.content, customersData?.content]);

  const paginationInfo = isSearchMode ? searchData : customersData;
  const loading = isSearchMode ? searchLoading : customersLoading;
  const error = isSearchMode
    ? searchError?.message || ""
    : customersError?.message || "";

  // Filter customers based on status
  const filteredCustomers = React.useMemo(() => {
    if (isSearchMode) return customers;

    switch (filter) {
      case "active":
        return customers.filter((customer) => customer.isActive);
      case "completed":
        return customers.filter((customer) => !customer.isActive);
      default:
        return customers;
    }
  }, [customers, filter, isSearchMode]);

  // Search handlers
  const handleSearch = () => {
    if (searchQuery.trim()) {
      setActiveSearchQuery(searchQuery.trim());
      setIsSearchMode(true);
      setCurrentPage(0);
    }
  };

  const handleClearSearch = () => {
    setSearchQuery("");
    setActiveSearchQuery("");
    setIsSearchMode(false);
    setCurrentPage(0);
  };

  const handleSearchInputKeyPress = (e) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };

  // Filter handlers
  const handleFilterChange = (newFilter) => {
    setFilter(newFilter);
    setCurrentPage(0);
  };

  // Pagination handlers
  const handlePageSizeChange = (newSize) => {
    setPageSize(newSize);
    setCurrentPage(0);
  };

  // Modal handlers
  const handleShowDetails = React.useCallback((customer) => {
    setSelectedCustomer(customer);
    setShowModal(true);
  }, []);

  const closeModal = () => {
    setShowModal(false);
    setSelectedCustomer(null);
  };

  // Get status badge for customer
  const getCustomerStatusBadge = React.useCallback((customer) => {
    if (customer.isActive) {
      return "inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800";
    } else {
      // Convert DaisyUI status badges to Tailwind
      const status = customer.paymentStatus;
      switch (status) {
        case "PAID":
          return "inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800";
        case "PENDING":
          return "inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800";
        case "FAILED":
          return "inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800";
        default:
          return "inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800";
      }
    }
  }, []);

  // Get status text for customer
  const getCustomerStatusText = React.useCallback(
    (customer) => {
      if (customer.isActive) {
        return t("customers.active");
      } else {
        return t(getPaymentStatusKey(customer.paymentStatus));
      }
    },
    [t]
  );

  // Define table columns for DataTable
  const tableColumns = React.useMemo(
    () => [
      {
        key: "customerName",
        header: t("customers.customerName"),
        render: (customer) => (
          <div className="flex items-center gap-3">
            <div className="flex items-center justify-center w-8 h-8 bg-gray-600 text-white rounded-full text-xs font-medium">
              {customer.fullName?.charAt(0)?.toUpperCase() || "?"}
            </div>
            <div>
              <div className="font-medium">{customer.fullName}</div>
              {customer.defaultLocation?.city && (
                <div className="text-sm text-gray-500">
                  {customer.defaultLocation.city}
                </div>
              )}
            </div>
          </div>
        ),
      },
      {
        key: "phone",
        header: t("customers.phone"),
        render: (customer) => (
          <span className="font-mono text-sm">{customer.phone}</span>
        ),
      },
      {
        key: "subscription",
        header: t("customers.subscription"),
        render: (customer) => (
          <span className="font-medium">{customer.subscriptionName}</span>
        ),
      },
      {
        key: "period",
        header: t("customers.period"),
        render: (customer) => (
          <div className="text-sm">
            <div>{formatDate(customer.startDate)}</div>
            <div className="text-gray-500">
              to {formatDate(customer.endDate)}
            </div>
          </div>
        ),
      },
      {
        key: "remainingDays",
        header: t("customers.remainingDays"),
        render: (customer) => (      <span
        className={`font-medium ${
          customer.remainingDaysCount > 7
            ? "text-green-600"
            : customer.remainingDaysCount > 0
            ? "text-yellow-600"
            : "text-red-600"
        }`}
      >
        {customer.remainingDaysCount || 0} {t("customers.days")}
      </span>
        ),
      },
      {
        key: "status",
        header: t("customers.status"),
        render: (customer) => (        <div className="flex flex-col gap-1">
          <div className={getCustomerStatusBadge(customer)}>
            {getCustomerStatusText(customer)}
          </div>
          {customer.isFrozenByUser && (
            <div className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
              {t("customers.frozen")}
            </div>
          )}
        </div>
        ),
      },
      {
        key: "actions",
        header: t("customers.actions"),
        render: (customer) => (
          <button
            className="px-3 py-1 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
            onClick={() => handleShowDetails(customer)}
          >
            {t("customers.viewDetails")}
          </button>
        ),
      },
    ],
    [t, getCustomerStatusBadge, getCustomerStatusText, handleShowDetails]
  );

  // Empty state component for customers table
  const emptyState = (
    <div className="text-center py-12">
      <div className="text-6xl mb-4">👥</div>
      <h3 className="text-xl font-semibold text-gray-700 mb-2">
        {t("customers.noCustomersFound")}
      </h3>
      <p className="text-gray-500">
        {isSearchMode
          ? t("customers.noSearchResults")
          : t("customers.noCustomersMessage")}
      </p>
    </div>
  );

  return (
    <div className="container mx-auto p-6 max-w-7xl">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          {t("customers.title")}
        </h1>
        <p className="text-gray-600">{t("customers.subtitle")}</p>
      </div>

      {/* Stats Cards */}
      <CustomerStatsCards stats={stats} isVisible={!isSearchMode} />

      {/* Map Toggle Section */}
      <div className="mt-8 flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">
            {t("customers.deliveryMap")}
          </h2>
          <p className="text-sm text-gray-600 mt-1">
            {t("customers.deliveryMapDescription")}
          </p>
        </div>
        <div className="flex items-center space-x-4">
          <div className="flex items-center space-x-2">
            <label
              htmlFor="map-date"
              className="text-sm font-medium text-gray-700"
            >
              {t("customers.selectDate")}:
            </label>
            <input
              id="map-date"
              type="date"
              value={
                selectedMapDate
                  ? selectedMapDate.toISOString().split("T")[0]
                  : ""
              }
              onChange={(e) =>
                setSelectedMapDate(
                  e.target.value ? new Date(e.target.value) : null
                )
              }
              className="border border-gray-300 rounded-md px-3 py-1 text-sm"
            />
          </div>
          <button
            onClick={() => setShowMap(!showMap)}
            className={`inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white ${
              showMap
                ? "bg-red-600 hover:bg-red-700 focus:ring-red-500"
                : "bg-blue-600 hover:bg-blue-700 focus:ring-blue-500"
            } focus:outline-none focus:ring-2 focus:ring-offset-2`}
          >
            <svg
              className="-ml-1 mr-2 h-4 w-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              {showMap ? (
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M6 18L18 6M6 6l12 12"
                />
              ) : (
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                />
              )}
            </svg>
            {showMap ? t("customers.hideMap") : t("customers.showMap")}
          </button>
        </div>
      </div>

      {/* Subscription Schedule Map */}
      {showMap && (
        <div className="mt-6">
          <SubscriptionScheduleMap
            restaurantId={restaurantId}
            selectedDate={selectedMapDate}
            language={t.language || "en"}
          />
        </div>
      )}

      {/* Search Bar */}
      <CustomerSearchBar
        searchQuery={searchQuery}
        onSearchQueryChange={setSearchQuery}
        onSearch={handleSearch}
        onClear={handleClearSearch}
        onKeyPress={handleSearchInputKeyPress}
        isSearchMode={isSearchMode}
        activeSearchQuery={activeSearchQuery}
        resultsCount={customers.length}
      />

      {/* Filter Tabs */}
      <CustomerFilterTabs
        filter={filter}
        onFilterChange={handleFilterChange}
        stats={stats}
        isVisible={!isSearchMode}
      />

      {/* Customers Table with Pagination */}
      <DataTable
        data={filteredCustomers}
        columns={tableColumns}
        rowKey={(customer) => customer.userSubscriptionId}
        isLoading={loading}
        error={error}
        emptyState={emptyState}
        pagination={{
          paginationInfo: paginationInfo,
          currentPage: currentPage,
          pageSize: pageSize,
          onPageChange: setCurrentPage,
          onPageSizeChange: handlePageSizeChange,
          isLoading: loading,
          hasError: !!error,
        }}
      />

      {/* Customer Details Modal */}
      <CustomerDetailsModal
        isOpen={showModal}
        customer={selectedCustomer}
        onClose={closeModal}
        getCustomerStatusBadge={getCustomerStatusBadge}
        getCustomerStatusText={getCustomerStatusText}
      />
    </div>
  );
};

export default Customers;
